import type { ExtractInjectionAPI } from '@atlaskit/editor-common/types/next-editor-plugin';
import { EditorState } from '@atlaskit/editor-prosemirror/state';

import type { AiSuggestionsPlugin } from '../aiSuggestionsPluginType';
import { getSuggestionDiffStepAttribution } from '../ui/utils/getSuggestionAgentAttribution';
import { applyToolCalls } from '../ui/utils/toolCalls/applyToolCalls';
import type { ShowSuggestionDiffAction } from './types';

export const showSuggestionDiffAction: (
	api: ExtractInjectionAPI<AiSuggestionsPlugin> | undefined,
) => ShowSuggestionDiffAction =
	(api: ExtractInjectionAPI<AiSuggestionsPlugin> | undefined) =>
	(id: string, options?: { hideDeletedDiffs?: boolean }): boolean => {
		let found = false;
		api?.core?.actions.execute(({ tr }) => {
			const suggestion = api?.aiSuggestions?.sharedState.currentState()?.suggestionById.get(id);
			if (!suggestion) {
				return tr;
			}
			found = true;
			// Apply tool calls to an isolated transaction so we can read its `steps`
			// and `before` for diff visualisation without affecting the source transaction.
			// PM nodes are immutable, so reusing tr.doc is safe.
			const isolatedTr = EditorState.create({ doc: tr.doc }).tr;
			const appliedTr = applyToolCalls({ tr: isolatedTr, suggestion });
			const stepAttribution = getSuggestionDiffStepAttribution(suggestion);

			api?.showDiff?.commands.showDiff({
				// Each step's positions live in the doc BEFORE that step is applied
				// (= `appliedTr.docs[i]`). Inverting every step against
				// `appliedTr.before` throws `RangeError` for any step past index 0
				// in a multi-tool-call suggestion. Reverse afterwards so the inverted
				// steps replay back to the original doc when the diff plugin applies
				// them to `appliedTr.doc`.
				stepsWithAttribution: appliedTr.steps
					.map((step, i) => ({
						step: step.invert(appliedTr.docs[i]),
						stepAttribution,
					}))
					.reverse(),
				originalDoc: appliedTr.doc,
				isInverted: true,
				diffType: 'step',
				hideDeletedDiffs: options?.hideDeletedDiffs,
				scrollIntoView: true,
			})({ tr });
			return tr;
		});
		return found;
	};
